import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { animated, useTransition } from '@react-spring/web';

interface AnimatedQuestionProps {
  currentIndex: number;
  askedOnSlideTitle: string | undefined;
  qfaQuestion: string;
  numberOfUpvotes: number;
}

interface QfaTransitionWrapperProps extends AnimatedQuestionProps {
  id: number;
  children: (props: AnimatedQuestionProps) => ReactNode;
}

export const QfaTransitionWrapper = ({
  id,
  currentIndex,
  askedOnSlideTitle,
  qfaQuestion,
  numberOfUpvotes,
  children,
}: QfaTransitionWrapperProps) => {
  const [prev, setPrev] = useState(currentIndex);

  useEffect(() => {
    setPrev(currentIndex);
  }, [currentIndex]);

  const next = prev <= currentIndex;

  const transitionConfig = useMemo(
    () => ({
      keys: (q: { id: number }) => q.id,
      initial: null,
      from: { transform: next ? 1 : -1, opacity: 0 },
      enter: { transform: 0, opacity: 1 },
      leave: { transform: next ? -1 : 1, opacity: 0 },
    }),
    [next],
  );

  const questionTransition = useTransition(
    { askedOnSlideTitle, qfaQuestion, id, numberOfUpvotes, currentIndex },
    transitionConfig,
  );

  return (
    <Box
      flex="1 1 auto"
      width="100%"
      alignItems="center"
      justifyContent="center"
      p="space1"
      height="100%"
    >
      {questionTransition(
        (
          { transform, opacity },
          { id, askedOnSlideTitle, qfaQuestion, numberOfUpvotes, currentIndex },
        ) => {
          return (
            <Box
              key={id}
              as={animated.div}
              alignItems="center"
              justifyContent="center"
              position="absolute"
              width="100%"
              height="100%"
              top={0}
              left={0}
              style={{
                transform: transform.to((t) => `translateY(${50 * t}%)`) as any,
                opacity: opacity as any,
              }}
            >
              {children({
                askedOnSlideTitle,
                qfaQuestion,
                numberOfUpvotes,
                currentIndex,
              })}
            </Box>
          );
        },
      )}
    </Box>
  );
};
